import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PresentationTabs from "../../../../../components/PresentationTabs";
import InstanceProducts from "../../../connect/products/InstanceProducts";
import {
  fetchCartHistory,
  fetchCartSnapshots,
  updateProductsToOrder,
} from "../../../../../services/orders";
import { AuthContext } from "../../../../../helpers/AuthContext";
import { normalizeInvoiceRoles } from "../../../../../utils/invoicePermissions";

const authHeaders = () => ({
  accessToken: localStorage.getItem("accessToken"),
});

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const firstPresentString = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const buildProjectDisplayTitle = (order = {}, fallback = "Project Cart") => {
  const customerName = firstPresentString(
    order?.Customer?.businessName,
    order?.Customer?.BusinessName,
    order?.Customer?.name,
    order?.customer?.businessName,
    order?.customer?.BusinessName,
    order?.customer?.name,
    order?.customerName,
    order?.CustomerName,
    order?.attachedCustomers?.[0]?.name,
    order?.attachedCustomers?.[0]?.businessName,
    order?.businessName,
    order?.BusinessName,
  );
  const projectNumber = firstPresentString(
    order?.projectNumber,
    order?.ProjectNumber,
    order?.projectNo,
    order?.ProjectNo,
    order?.ProjectId,
    order?.projectId,
    order?.OrderId,
    order?.id,
  );

  if (customerName && projectNumber) return `${customerName} — Project #${projectNumber}`;
  if (customerName) return customerName;
  if (projectNumber) return `Project #${projectNumber}`;
  return fallback;
};

const normalizeOrderForCart = (rawOrder = {}) => {
  const normalizedItems = normalizeArray(rawOrder.Items).map((item) => ({
    ...item,
    pricings: normalizeArray(item?.pricings),
    decorations: normalizeArray(item?.decorations),
    artworks: normalizeArray(item?.artworks),
  }));
  const normalizedProducts = normalizeArray(rawOrder.Products).map((item) => ({
    ...item,
    pricings: normalizeArray(item?.pricings),
    decorations: normalizeArray(item?.decorations),
    artworks: normalizeArray(item?.artworks),
  }));
  const canonicalItems = normalizedProducts.length ? normalizedProducts : normalizedItems;

  return {
    ...rawOrder,
    AssignedTo: normalizeArray(rawOrder.AssignedTo),
    Items: canonicalItems,
    Products: canonicalItems,
    OtherService: normalizeArray(rawOrder.OtherService),
    OtherServices: normalizeArray(rawOrder.OtherServices),
  };
};

const activityTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

function CartHistoryDialog({ open, onClose, orderId }) {
  const [tab, setTab] = useState("activity");
  const [loading, setLoading] = useState(false);
  const [activity, setActivity] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !orderId) return;
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      fetchCartHistory(orderId),
      fetchCartSnapshots(orderId),
    ])
      .then(([historyRes, snapshotRes]) => {
        if (!active) return;
        setActivity(Array.isArray(historyRes?.history) ? historyRes.history : []);
        setSnapshots(Array.isArray(snapshotRes?.snapshots) ? snapshotRes.snapshots : []);
      })
      .catch((err) => {
        if (active) setError(err?.response?.data?.message || "Failed to load cart history.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, orderId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Cart History</Typography>
          <IconButton onClick={onClose} aria-label="Close cart history"><CloseRoundedIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ mb: 2 }}>
          <Tab value="activity" label={`Activity (${activity.length})`} />
          <Tab value="snapshots" label={`Snapshots (${snapshots.length})`} />
        </Tabs>
        {loading ? <CircularProgress size={24} /> : null}
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {!loading && tab === "activity" ? (
          activity.length ? (
            <List dense disablePadding>
              {activity.map((item, index) => (
                <Box key={`${item?.timestamp || index}-${item?.action || "activity"}`}>
                  <ListItemText
                    primary={item?.message || item?.action || "Cart update"}
                    secondary={[item?.userName || item?.userEmail || null, activityTime(item?.timestamp)].filter(Boolean).join(" - ")}
                  />
                  {index < activity.length - 1 ? <Divider /> : null}
                </Box>
              ))}
            </List>
          ) : (
            <Alert severity="info">No cart activity has been recorded yet.</Alert>
          )
        ) : null}
        {!loading && tab === "snapshots" ? (
          snapshots.length ? (
            <List dense disablePadding>
              {snapshots.map((item, index) => (
                <Box key={`${item?.cartSnapshotId || index}-${item?.documentId || item?.estimationId || "snapshot"}`}>
                  <ListItemText
                    primary={`${item?.status || "Snapshot"}${item?.revisionNumber ? ` - Revision ${item.revisionNumber}` : ""}`}
                    secondary={[
                      item?.cartSnapshotId ? `Snapshot ${item.cartSnapshotId}` : null,
                      item?.itemCount != null ? `${item.itemCount} item${Number(item.itemCount) === 1 ? "" : "s"}` : null,
                      activityTime(item?.updatedAt || item?.cartSnapshotCreatedAt),
                    ].filter(Boolean).join(" - ")}
                  />
                  {index < snapshots.length - 1 ? <Divider /> : null}
                </Box>
              ))}
            </List>
          ) : (
            <Alert severity="info">No cart snapshots have been recorded yet.</Alert>
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function OrderCartPanel({ orderId, overviewPath, instanceUniqueId }) {
  const { authState } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showProducts, setShowProducts] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchOrder = useCallback(async (options = {}) => {
    if (!orderId) return;
    // Keep the existing cart mounted while background mutations rehydrate it.
    if (!options.suppressLoader) setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/get-order/${orderId}`,
        { headers: authHeaders() }
      );
      const normalized = normalizeOrderForCart(response?.data?.order || {});
      setOrder(normalized);
      setSelectedProducts(normalized.Products || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load project cart.");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const updateItemsToOrder = useCallback(async (products = []) => {
    if (!order) return;
    const payload = {
      customer_name: order.CustomerName,
      email: order.Email,
      phone: order.Phone,
      total: order.Total,
      items: products.map((product) => ({
        ...product,
        orderItemId:
          product?.orderItemId ??
          product?.OrderItemId ??
          (product?.id != null && product?.ProductId != null ? product.id : null),
      })),
      siteURL: order.SiteURL,
      order_status: order.OrderStatus,
      subStatusId: order?.subStatusId ?? null,
      assigned_to: order.AssignedTo,
      AttachedCustomerId: order?.attachedCustomers?.length ? order.attachedCustomers[0] : "",
      clientContactId: order?.clientContactId || null,
    };
    await updateProductsToOrder(orderId, payload);
    setSelectedProducts(products);
    await fetchOrder();
  }, [fetchOrder, order, orderId]);

  const projectDisplayTitle = useMemo(() => buildProjectDisplayTitle(order, "Project Cart"), [order]);
  const isSuperAdministrator = useMemo(
    () => normalizeInvoiceRoles(authState || {}).has("super-administrator"),
    [authState]
  );

  if (!orderId) {
    return <Alert severity="warning">Project id is unavailable for this cart.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>{projectDisplayTitle}</Typography>
          <Typography variant="body2" color="text.secondary">
            Products, services, pricing, cart history, and summary for {projectDisplayTitle}.
          </Typography>
        </Box>
        {overviewPath ? (
          <Button component="a" href={overviewPath} target="_blank" rel="noopener noreferrer" variant="outlined" endIcon={<OpenInNewRoundedIcon />} sx={{ alignSelf: { xs: "stretch", sm: "center" } }}>
            Open Project Overview in New Tab
          </Button>
        ) : null}
      </Stack>

      {loading && !order ? <CircularProgress size={24} /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {order ? (
        <Box sx={{ maxHeight: "58vh", overflow: "auto", pr: 0.5 }}>
          <PresentationTabs
            fetchOrder={fetchOrder}
            order={order}
            selectedProducts={selectedProducts}
            handleShowProductsPopup={() => setShowProducts(true)}
            onOpenCartHistory={() => setHistoryOpen(true)}
            canSeeManualPricingToggle={isSuperAdministrator}
          />
        </Box>
      ) : null}

      <Dialog open={showProducts} onClose={() => setShowProducts(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Add Product</Typography>
            <IconButton onClick={() => setShowProducts(false)} aria-label="Close add product dialog">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, minHeight: "70vh" }}>
          {order ? (
            <InstanceProducts
              updateItemsToOrder={updateItemsToOrder}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              fetchOrder={fetchOrder}
              isPresentation
              popupMode={showProducts}
              embedded
              closeProductListPopup={() => setShowProducts(false)}
              order={order}
              instanceUniqueId={instanceUniqueId}
            />
          ) : loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "70vh" }}>
              <CircularProgress aria-label="Loading products" />
            </Stack>
          ) : (
            <Alert severity="error" sx={{ m: 2 }}>Unable to load the order before adding products.</Alert>
          )}
        </DialogContent>
      </Dialog>

      <CartHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        orderId={orderId}
      />
    </Stack>
  );
}
